import React, { useEffect, useState } from 'react'

import { useMicPermission } from '../hooks/useMicPermission'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'
import { HiExclamation } from 'react-icons/hi'

import { StaticBanner } from '@/features/product-messages'
import { BUTTON_PRESS, VIEW, trackEvent } from '@/utils/analytics'

import { MicPermissionModal } from './MicPermissionModal'

/**
 * Analytics name for tracking mic permission banner events
 */
const ANALYTIC_NAME = 'Mic Permission Banner' as const

/**
 * MicPermissionBanner component displays a persistent banner when
 * microphone permissions are not granted and emergency calling is enabled.
 *
 * "Enable" opens the guided panel rather than calling `getUserMedia` straight
 * from the banner: the request only raises the browser dialog from the
 * `prompt` state, so in every other state a bare request is a button that
 * visibly does nothing. The panel is what tells the operator which of the
 * site settings or a headset actually fixes their case (ENG-32960).
 *
 * The logged-out case is handled by `AppLayout`, which does not mount any
 * banner until the session is valid (ENG-34127).
 *
 * @returns A banner component or null if conditions are not met
 */
export const MicPermissionBanner = () => {
  const isEmergencyCallEnabled = useFeatureFlag(FeatureFlag.emergencyCall)
  const { micState, isChecking, requestPermission } = useMicPermission({
    isEnabled: isEmergencyCallEnabled,
  })
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)

  const shouldShowBanner =
    isEmergencyCallEnabled &&
    !isChecking &&
    micState !== null &&
    micState !== 'granted'

  // Track when the banner is shown to the user
  useEffect(() => {
    if (shouldShowBanner) {
      trackEvent(`${VIEW} ${ANALYTIC_NAME}`)
    }
  }, [shouldShowBanner])

  const handleEnableClick = () => {
    trackEvent(`${BUTTON_PRESS} ${ANALYTIC_NAME} - Enable`)
    setIsModalOpen(true)
  }

  const handleRequestPermission = async () => {
    setIsRequesting(true)
    try {
      await requestPermission()
    } finally {
      setIsRequesting(false)
    }
  }

  if (!shouldShowBanner) {
    return null
  }

  return (
    <>
      <StaticBanner
        dismissible={false}
        icon={<HiExclamation />}
        message={t('emergency_call:mic_banner_message')}
        primaryButton={{
          onClick: handleEnableClick,
          text: t('emergency_call:mic_banner_cta'),
        }}
        status='error'
      />
      <MicPermissionModal
        isBusy={isRequesting}
        isOpen={isModalOpen}
        micState={micState}
        onClose={() => setIsModalOpen(false)}
        onRequestPermission={handleRequestPermission}
      />
    </>
  )
}
